import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TaskCard } from "@/components/TaskCard";
import { STATUS_META, fmtDate, todayISO } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Loader2, CalendarX, ChevronLeft, ChevronRight } from "lucide-react";

const STATUSES = ["pendiente", "en_progreso", "completada", "en_revision", "vencida"];
const WEEK_LABELS = ["L", "M", "X", "J", "V", "S", "D"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  return date;
}

export default function Tasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("lista");
  const [anchor, setAnchor] = useState(todayISO());

  const filters = {
    assignee_id: params.get("assignee_id") || "",
    department_id: params.get("department_id") || "",
    status: params.get("status") || "",
    priority: params.get("priority") || "",
  };

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const qs = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => v && qs.set(k, v));

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["tasks", filters],
    queryFn: () => api.get(`/tasks?${qs.toString()}`).then((r) => r.data),
  });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/employees").then((r) => r.data), enabled: isAdmin });
  const { data: departments = [] } = useQuery({ queryKey: ["departments"], queryFn: () => api.get("/departments").then((r) => r.data) });

  const grouped = useMemo(() => {
    const g = {};
    tasks.forEach((t) => { (g[t.date] = g[t.date] || []).push(t); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [tasks]);

  const weekDays = useMemo(() => {
    const s = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(s); d.setDate(s.getDate() + i);
      return d.toISOString().slice(0, 10);
    });
  }, [anchor]);

  const dayTasks = tasks.filter((t) => t.date === anchor).sort((a, b) => a.time.localeCompare(b.time));

  const byDate = useMemo(() => {
    const m = {};
    tasks.forEach((t) => { (m[t.date] = m[t.date] || []).push(t); });
    return m;
  }, [tasks]);

  const byMonth = useMemo(() => {
    const m = {};
    tasks.forEach((t) => { const k = t.date.slice(0, 7); (m[k] = m[k] || []).push(t); });
    return m;
  }, [tasks]);

  const monthCells = useMemo(() => {
    const d = new Date(anchor + "T00:00:00");
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(d.getFullYear(), d.getMonth(), 1 - offset);
    return Array.from({ length: 42 }, (_, i) => {
      const c = new Date(start); c.setDate(start.getDate() + i);
      return c.toISOString().slice(0, 10);
    });
  }, [anchor]);

  const shiftMonth = (delta) => { const d = new Date(anchor + "T00:00:00"); d.setDate(1); d.setMonth(d.getMonth() + delta); setAnchor(d.toISOString().slice(0, 10)); };
  const shiftYear = (delta) => { const d = new Date(anchor + "T00:00:00"); d.setFullYear(d.getFullYear() + delta); setAnchor(d.toISOString().slice(0, 10)); };
  const monthLabel = new Date(anchor + "T00:00:00").toLocaleDateString("es", { month: "long", year: "numeric" });
  const yearLabel = anchor.slice(0, 4);
  const monthName = (mi) => new Date(2020, mi, 1).toLocaleDateString("es", { month: "long" });

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Tareas</h1>
        <p className="text-sm text-slate-500">Calendario y seguimiento de tareas</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {isAdmin && (
          <Select value={filters.assignee_id || "all"} onValueChange={(v) => setFilter("assignee_id", v)}>
            <SelectTrigger className="w-40 bg-white" data-testid="filter-assignee"><SelectValue placeholder="Funcionario" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los funcionarios</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={filters.department_id || "all"} onValueChange={(v) => setFilter("department_id", v)}>
          <SelectTrigger className="w-36 bg-white" data-testid="filter-area"><SelectValue placeholder="Área" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las áreas</SelectItem>
            {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v)}>
          <SelectTrigger className="w-36 bg-white" data-testid="filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.priority || "all"} onValueChange={(v) => setFilter("priority", v)}>
          <SelectTrigger className="w-32 bg-white" data-testid="filter-priority"><SelectValue placeholder="Prioridad" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toda prioridad</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="alta">Alta</SelectItem>
            <SelectItem value="urgente">Urgente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList data-testid="view-tabs">
          <TabsTrigger value="dia" data-testid="tab-dia">Día</TabsTrigger>
          <TabsTrigger value="semana" data-testid="tab-semana">Semana</TabsTrigger>
          <TabsTrigger value="mes" data-testid="tab-mes">Mes</TabsTrigger>
          <TabsTrigger value="anio" data-testid="tab-anio">Año</TabsTrigger>
          <TabsTrigger value="lista" data-testid="tab-lista">Lista</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-16 text-slate-400">
          <CalendarX className="mb-2 h-8 w-8" />
          <p className="text-sm">No hay tareas con estos filtros</p>
        </div>
      ) : view === "lista" ? (
        <div className="space-y-6">
          {grouped.map(([date, items]) => (
            <div key={date}>
              <h3 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-slate-400">{fmtDate(date)}</h3>
              <div className="grid gap-3 md:grid-cols-2">
                {items.map((t, i) => <TaskCard key={t.id} task={t} index={i} />)}
              </div>
            </div>
          ))}
        </div>
      ) : view === "dia" ? (
        <div className="space-y-3">
          <input type="date" value={anchor} onChange={(e) => setAnchor(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="day-picker" />
          {dayTasks.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">Sin tareas para este día</p>
          ) : (
            dayTasks.map((t, i) => (
              <div key={t.id} className="flex gap-3">
                <div className="w-14 shrink-0 pt-4 text-right text-xs font-bold text-slate-400">{t.time}</div>
                <div className="flex-1"><TaskCard task={t} index={i} /></div>
              </div>
            ))
          )}
        </div>
      ) : view === "semana" ? (
        <div className="grid grid-cols-7 gap-2 overflow-x-auto">
          {weekDays.map((d) => {
            const items = tasks.filter((t) => t.date === d);
            const isToday = d === todayISO();
            return (
              <div key={d} className={cn("min-w-[110px] rounded-xl border bg-white p-2", isToday ? "border-slate-900" : "border-slate-200")}>
                <div className={cn("mb-2 text-center text-xs font-bold", isToday ? "text-slate-900" : "text-slate-400")}>
                  {new Date(d + "T00:00:00").toLocaleDateString("es", { weekday: "short", day: "2-digit" })}
                </div>
                <div className="space-y-1.5">
                  {items.map((t) => {
                    const m = STATUS_META[t.status];
                    return (
                      <a key={t.id} href={`/tareas/${t.id}`} className={cn("block rounded-lg border-l-4 bg-slate-50 p-1.5 text-[11px] font-medium leading-tight text-slate-700 hover:bg-slate-100", m.leftBorder)}>
                        <span className="line-clamp-2">{t.title}</span>
                        <span className="text-slate-400">{t.time}</span>
                      </a>
                    );
                  })}
                  {items.length === 0 && <div className="py-2 text-center text-[10px] text-slate-300">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : view === "mes" ? (
        <div className="space-y-3" data-testid="month-view">
          <div className="flex items-center justify-between">
            <button onClick={() => shiftMonth(-1)} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50" data-testid="month-prev"><ChevronLeft className="h-4 w-4" /></button>
            <span className="font-heading text-base font-bold capitalize text-slate-900">{monthLabel}</span>
            <button onClick={() => shiftMonth(1)} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50" data-testid="month-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold uppercase text-slate-400">
            {WEEK_LABELS.map((w) => <div key={w}>{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthCells.map((cell) => {
              const items = byDate[cell] || [];
              const inMonth = cell.slice(0, 7) === anchor.slice(0, 7);
              const isToday = cell === todayISO();
              const dots = [...new Set(items.map((t) => t.status))].slice(0, 4);
              return (
                <button key={cell} data-testid={`month-cell-${cell}`}
                  onClick={() => { setAnchor(cell); setView("dia"); }}
                  className={cn(
                    "flex min-h-[62px] flex-col rounded-lg border p-1.5 text-left transition hover:border-slate-400",
                    inMonth ? "bg-white" : "bg-slate-50/50 opacity-50",
                    isToday ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"
                  )}>
                  <span className={cn("text-xs font-bold", isToday ? "text-slate-900" : "text-slate-500")}>{Number(cell.slice(8, 10))}</span>
                  <div className="mt-1 flex flex-wrap gap-0.5">
                    {dots.map((s) => <span key={s} className={cn("h-1.5 w-1.5 rounded-full", STATUS_META[s].dot)} />)}
                  </div>
                  {items.length > 0 && <span className="mt-auto text-[10px] font-semibold text-slate-400">{items.length} tarea{items.length > 1 ? "s" : ""}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="year-view">
          <div className="flex items-center justify-between">
            <button onClick={() => shiftYear(-1)} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50" data-testid="year-prev"><ChevronLeft className="h-4 w-4" /></button>
            <span className="font-heading text-base font-bold text-slate-900">{yearLabel}</span>
            <button onClick={() => shiftYear(1)} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50" data-testid="year-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 12 }).map((_, mi) => {
              const key = `${yearLabel}-${String(mi + 1).padStart(2, "0")}`;
              const items = byMonth[key] || [];
              const done = items.filter((t) => t.status === "completada").length;
              const overdue = items.filter((t) => t.status === "vencida").length;
              return (
                <button key={key} data-testid={`year-month-${key}`}
                  onClick={() => { setAnchor(`${key}-01`); setView("mes"); }}
                  className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                  <div className="font-heading text-sm font-bold capitalize text-slate-700">{monthName(mi)}</div>
                  <div className="mt-1 font-heading text-3xl font-extrabold text-slate-900">{items.length}</div>
                  <div className="mt-1 flex gap-3 text-[11px]">
                    <span className="text-emerald-600">{done} ✓</span>
                    <span className="text-red-500">{overdue} vencidas</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
